/**
 * MyLessonsPage — the member's lesson packages at the current club.
 *
 * Route: `/plans` (holdings page, titled "My Plans"). `/lessons/my` redirects here.
 *
 * @module pages/lessons/MyLessonsPage
 */

import { useMemo, type ReactNode } from 'react';
import { asTimezone, asUtcDate, formatClubDate, type Timezone } from '@butter/library/datetime';
import { GraduationIcon, ChevronIcon } from '@butter/ui/icons';
import { EmptyState, SectionErrorState, SectionSkeleton, CardSkeleton } from '@butter/ui/components';
import { Button, Skeleton } from '@butter/ui/primitives';
import { TenantLink } from '~/lib/navigation';
import { useAuth } from '~/services/auth';
import { useClubQuery } from '~/services/internalApi/club/hooks';
import { useHashModal } from '~/hooks/useHashModal';
import { useUserLessons, type UserLesson } from '~/services/internalApi/lessons';
import {
  LESSON_PACK_STATUS_LABEL,
  isLessonPackTombstoned,
  lessonPackName,
  splitLessonPacks,
} from '@butter/library/lessons';
import { formatRemainingHeadline } from './lessonPackDisplay';
import { LessonPageHeader } from './components/LessonPageHeader';
import { LessonPackPanel } from './components/LessonPackPanel';
import { LessonPackCard } from './components/LessonPackCard';

// Shared by both "no answer yet" branches below — a first load and an
// unresolved-but-not-erroring wait render identically.
function LessonsLoading() {
  return (
    <div aria-busy="true" aria-live="polite" data-testid="lessons-loading">
      <span className="sr-only">Loading your lesson packages…</span>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-x-20">
        <Skeleton className="h-64 w-full rounded-28" />
        <SectionSkeleton showSubtitle>
          <CardSkeleton />
        </SectionSkeleton>
      </div>
    </div>
  );
}

export function MyLessonsPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: club } = useClubQuery();
  const timezone = club?.timezone ?? asTimezone('UTC');
  const {
    userLessons: packs,
    hasAnswer,
    failure,
    isLoading: queryLoading,
    error,
    isPaused,
    refetch,
  } = useUserLessons({ enabled: isAuthenticated });

  const { current, history } = useMemo(() => splitLessonPacks(packs), [packs]);

  // Opens the shared SignInModal MainLayout already mounts at this hash key —
  // never a second modal instance.
  const signInModal = useHashModal('dialog-sign-in');

  const isLoading = authLoading || queryLoading;

  // Deliberately NOT loading → error → empty → content: the hook keeps the
  // last good packs through a failed refetch, so `failure` here is always a
  // first-load failure and good cached packs still render.
  //
  // Signed-out is gated on `!authLoading` too (a refinement on top of the
  // literal isAuthenticated check) so a session still restoring from a
  // stored token doesn't flash "sign in" before flipping to loading/content.
  //
  // The header renders once, above every branch below, so it never jumps or
  // remounts as the state changes.
  function renderBody(): ReactNode {
    // 1. Signed out — the query never fired (enabled: isAuthenticated).
    if (!isAuthenticated && !authLoading) {
      return (
        <EmptyState
          variant="placeholder"
          data-testid="lessons-signin-empty"
          icon={<GraduationIcon ariaHidden className="size-8 text-butter-gray-700" />}
          title="Sign in to see your plans"
          action={
            <Button variant="theme" size="xl" onClick={signInModal.open} data-testid="lessons-signin-button">
              Sign in
            </Button>
          }
        />
      );
    }

    // 2. Loading — no answer YET.
    if (isLoading) {
      return <LessonsLoading />;
    }

    // 3. No usable answer is coming without help — a refusal envelope, a
    // thrown request, or an offline pause (isPaused, error still null).
    if (failure || (!hasAnswer && (error || isPaused))) {
      return (
        <div data-testid="lessons-error">
          <SectionErrorState onRetry={refetch} />
        </div>
      );
    }

    // Still no answer (e.g. clubId not resolved yet, neither erroring nor
    // paused) — treat as loading rather than falling through to "empty".
    if (!hasAnswer) {
      return <LessonsLoading />;
    }

    // 4. Empty — a real, empty answer.
    if (packs.length === 0) {
      return (
        <EmptyState
          variant="placeholder"
          data-testid="lessons-empty"
          icon={<GraduationIcon ariaHidden className="size-8 text-butter-gray-700" />}
          title="No Active Lesson Packages"
          description="Explore available lesson packages and find the one that fits you best"
          action={
            <TenantLink to="/lessons" data-testid="lessons-empty-browse-link" className="btn btn-primary btn-xl">
              View Lesson Packages
            </TenantLink>
          }
        />
      );
    }

    // 5. Content — stale-but-good still renders here too.
    return (
      <>
        {current.length > 0 && (
          <section aria-labelledby="lessons-current-heading">
            <h2 id="lessons-current-heading" className="sr-only">
              Current packages
            </h2>
            <div className="space-y-10">
              {current.map((pack) => (
                <LessonPackPanel key={pack.id} pack={pack} timezone={timezone} />
              ))}
            </div>
          </section>
        )}

        {history.length > 0 && (
          <details data-testid="lessons-history" className="group mt-12">
            <summary
              data-testid="lessons-history-toggle"
              className="flex cursor-pointer list-none items-center justify-between rounded-xl px-4 py-3 text-xl font-semibold text-fg-primary text-interactive:bg-butter-gray-50"
            >
              Past packages ({history.length})
              <ChevronIcon ariaHidden className="size-5 rotate-90 transition-transform group-open:-rotate-90" />
            </summary>
            <ul data-testid="lessons-history-list" className="mt-6 grid gap-6 sm:grid-cols-2">
              {history.map((pack) => (
                <li key={pack.id}>
                  <HistoryCard pack={pack} timezone={timezone} />
                </li>
              ))}
            </ul>
          </details>
        )}
      </>
    );
  }

  return (
    <>
      <LessonPageHeader title="My Plans" />
      {renderBody()}
    </>
  );
}

/**
 * One compact card in the collapsed history list. A spent/expired/canceled
 * pack has nothing to spend, so — unlike `LessonPackPanel` — it renders only
 * the card, no details block and no instructor list.
 */
function HistoryCard({ pack, timezone }: { pack: UserLesson; timezone: Timezone }) {
  return (
    <LessonPackCard
      packId={pack.id}
      name={lessonPackName(pack)}
      description={pack.lesson?.description}
      colorName={pack.lesson?.color}
      statusLabel={LESSON_PACK_STATUS_LABEL[pack.status]}
      figureLabel={formatRemainingHeadline(pack.remainingMinutes)}
      purchasedLabel={formatClubDate(asUtcDate(new Date(pack.purchaseDate)), timezone, 'MMM d, yyyy')}
      isTombstoned={isLessonPackTombstoned(pack)}
      compact
    />
  );
}
